import { useCallback, useMemo, useState } from 'react';
import { isFunction } from '@direct-frontend/stdlib';
import {
    type OpenApiGetIntegrationsTypeStatusSearchParams,
    type OpenApiSchemaIntegrationStatus,
    type OpenApiSchemaIntegrationSyncType,
    type OpenApiSchemaIntegrationType,
    type OpenApiSchemaMapping,
} from '#shared/api/schema';
import { protect } from '#shared/lib/function';
import { showError, showInfo } from '#shared/lib/toast';
import {
    startIntegration,
    startIntegrationDryMapping,
    startIntegrationFeedbacks,
    startIntegrationMapping,
    startIntegrationMCFeed,
    syncExternalStoreWarehouses,
    useLazyGetIntegrationStatus,
} from '../api';

type IntegrationPollingKey = `${OpenApiSchemaIntegrationSyncType}:${OpenApiSchemaIntegrationType | 'all'}`;

const getIntegrationPollingKey = (
    syncType: OpenApiSchemaIntegrationSyncType,
    integrationType: OpenApiSchemaIntegrationType | undefined
): IntegrationPollingKey => `${syncType}:${integrationType ?? 'all'}`;

/** Таймер на отложенный запуск первой проверки статуса интеграции по типам */
const refetchHistoryTimeout: Partial<Record<IntegrationPollingKey, ReturnType<typeof setTimeout>>> = {};

/** Таймер на отложенный запуск интервальных проверок статуса интеграции по типам */
const refetchHistoryRepeatedInterval: Partial<Record<IntegrationPollingKey, ReturnType<typeof setInterval>>> = {};

/** Булевый флаг активного выполнения запроса для предотвращения повторных запросов */
const pollingRequestsInProgressState: Partial<Record<IntegrationPollingKey, boolean>> = {};

const getToastMessageBySyncType = (syncType: OpenApiSchemaIntegrationSyncType) => {
    switch (syncType) {
        case 'orders':
            return 'Импорт блока "заказы" завершён.';

        case 'prices':
            return 'Импорт блока "цены" завершён.';

        case 'products':
            return 'Импорт блока "товары" завершён.';

        case 'mapping':
            return 'Сопоставление товаров завершено.';

        case 'stocks':
            return 'Импорт блока "остатки" завершён.';

        case 'excel':
            return 'Импорт товаров из EXCEL завершён.';

        case 'mc_feed':
            return 'Выгрузка фидов из Яндекс Товаров завершена.';

        case 'feedbacks':
            return 'Обновление отзывов завершено.';

        case 'user_import':
            return 'Импорт пользователей завершён.';

        case 'warehouses':
            return 'Синхронизация складов завершена.';

        default:
            return '';
    }
};

const startIntegrationBySyncType = async (
    syncType: OpenApiSchemaIntegrationSyncType,
    integrationType: OpenApiSchemaIntegrationType | undefined,
    mapping: OpenApiSchemaMapping | undefined
) => {
    switch (syncType) {
        case 'excel':
            return false;
        case 'mc_feed':
            await startIntegrationMCFeed();

            return true;
        case 'mapping':
            if (!integrationType) {
                throw new Error('Не указан источник данных для запуска сопоставления товаров');
            }

            await startIntegrationMapping({ integration_type: integrationType });

            return true;
        case 'dry_mapping':
            if (integrationType && mapping) {
                await startIntegrationDryMapping({
                    params: { integration_type: integrationType },
                    payload: { mapping },
                });

                return true;
            }

            await startIntegration({ type: syncType });

            return true;
        case 'feedbacks':
            if (!integrationType) {
                throw new Error('Не указан тип интеграции для запуска синхронизации отзывов');
            }

            await startIntegrationFeedbacks({ integration_type: integrationType });

            return true;
        case 'warehouses':
            if (integrationType === 'external_store') {
                await syncExternalStoreWarehouses();

                return true;
            }

            await startIntegration({ type: syncType });

            return true;

        default:
            await startIntegration({ type: syncType });

            return true;
    }
};

export type UseStartIntegrationOptions = {
    onFinishCallback?: (
        syncType: OpenApiSchemaIntegrationSyncType,
        lastIntegration?: OpenApiSchemaIntegrationStatus
    ) => void;
    /** Метод вызывается после каждого запроса статуса интеграции */
    onPollingCallback?: (
        syncType: OpenApiSchemaIntegrationSyncType,
        lastIntegration?: OpenApiSchemaIntegrationStatus
    ) => void;
    /** Система для запуска интеграции и фильтрации её истории. */
    integrationType?: OpenApiSchemaIntegrationType;
    /** При `syncType === 'dry_mapping'` - данные для сопоставления при тестовом запуске. */
    mapping?: OpenApiSchemaMapping;
};

export const buildIntegrationsTypeStatusSearchParams = (
    syncType: OpenApiSchemaIntegrationSyncType,
    integrationType: OpenApiSchemaIntegrationType | undefined,
    extra?: Pick<OpenApiGetIntegrationsTypeStatusSearchParams, 'page' | 'per_page'>
): OpenApiGetIntegrationsTypeStatusSearchParams => {
    return {
        sync_type: [syncType],
        ...extra,
        ...((syncType === 'feedbacks' ||
            syncType === 'warehouses' ||
            syncType === 'mc_feed' ||
            syncType === 'mapping' ||
            syncType === 'dry_mapping') &&
        integrationType
            ? { system_type: [integrationType] }
            : {}),
    };
};

export const useStartIntegration = (
    syncType: OpenApiSchemaIntegrationSyncType,
    isFetching: boolean,
    options: UseStartIntegrationOptions = {}
) => {
    /** Флаг, запускающий интервальный поллинг данных на предмет окончания интеграции */
    const [canPolling, setCanPollingFlag] = useState(false);
    /** Выполняется ли интеграция на текущий момент */
    const [isProcessing, toggleProcessing] = useState(isFetching);

    const [fetchHistory] = useLazyGetIntegrationStatus({});

    const { integrationType, onFinishCallback, mapping, onPollingCallback } = options;
    const pollingKey = getIntegrationPollingKey(syncType, integrationType);

    const integrationStatusSearchParams = useMemo(
        () => buildIntegrationsTypeStatusSearchParams(syncType, integrationType),
        [syncType, integrationType]
    );

    /** При анмаунтах компонента необходим очищать ресурсы */
    const handleClean = useCallback(() => {
        if (refetchHistoryTimeout[pollingKey]) {
            clearTimeout(refetchHistoryTimeout[pollingKey]);
            refetchHistoryTimeout[pollingKey] = undefined;
        }

        if (refetchHistoryRepeatedInterval[pollingKey]) {
            clearInterval(refetchHistoryRepeatedInterval[pollingKey]);
            refetchHistoryRepeatedInterval[pollingKey] = undefined;
        }

        /** Сбрасываем лоадер и флаги поллинга при принудительной очистке (закрытие модалки и т.п.) */
        toggleProcessing(false);
        setCanPollingFlag(false);
        pollingRequestsInProgressState[pollingKey] = false;
    }, [pollingKey]);

    const finishIntegration = useCallback(
        async (
            fetchHistory: ReturnType<typeof useLazyGetIntegrationStatus>[0],
            lastIntegration?: OpenApiSchemaIntegrationStatus
        ) => {
            /** Очищаем интервальный таймер проверки статуса интеграции */
            refetchHistoryRepeatedInterval[pollingKey] && clearInterval(refetchHistoryRepeatedInterval[pollingKey]);

            /** Вручную перезапрашиваем историю интеграций после завершения последней, чтобы актуализировать состояние */
            await protect(async () => await fetchHistory(integrationStatusSearchParams));

            /** Убираем лоадер с кнопки */
            toggleProcessing(false);

            /** Сбрасываем флаг для поллинга будущих интеграций */
            setCanPollingFlag(false);

            /** Сбрасываем предыдущие значения таймеров для возможности повторного запуска */
            refetchHistoryRepeatedInterval[pollingKey] = undefined;
            refetchHistoryTimeout[pollingKey] = undefined;

            /** Сбрасываем состояние запущенного запроса, если оно успело установиться */
            pollingRequestsInProgressState[pollingKey] = false;

            onFinishCallback?.(syncType, lastIntegration);

            /** Уведомляем пользователя об окончании интеграции */
            const toastMessage = getToastMessageBySyncType(syncType);

            toastMessage && showInfo(toastMessage);
        },
        [syncType, pollingKey, onFinishCallback, integrationStatusSearchParams]
    );

    const fetchHistoryAndPollingOrFinishIntegration = useCallback(async () => {
        await protect(async () => {
            /** Смотрим на статус последней запущенной интеграции */

            const { data } = await fetchHistory(
                buildIntegrationsTypeStatusSearchParams(syncType, integrationType, { per_page: 1 })
            );

            const isLastRequestEnded = data?.[0]?.status !== 'in_progress' && data?.[0]?.status !== 'not_running';

            if (isLastRequestEnded) {
                /** Последняя запущенная интеграция к моменту проверки успела завершиться */
                await finishIntegration(fetchHistory, data?.[0]);
            } else {
                /**
                 * Последняя запущенная интеграция к моменту проверки все еще в процессе:
                 * - актуализируем список запущенных интеграций
                 * - выставляем флаг поллинга запущенных интеграций
                 */
                await fetchHistory(integrationStatusSearchParams);
                setCanPollingFlag(true);
                onPollingCallback?.(syncType, data?.[0]);
            }
        });
    }, [
        setCanPollingFlag,
        finishIntegration,
        syncType,
        integrationType,
        integrationStatusSearchParams,
        fetchHistory,
        onPollingCallback,
    ]);

    /** Периодический опрос статуса завершения запущенной интеграции */
    const handlePolling = useCallback(async () => {
        /** Если интервальная проверка уже была запущена ранее - не запускаем больше */
        if (refetchHistoryRepeatedInterval[pollingKey]) {
            return;
        }

        try {
            /** Запускаем интервальную проверку окончания интеграции через каждые 'REFETCH_IN_PROGRESS_TIMEOUT' секунд */
            const REFETCH_IN_PROGRESS_TIMEOUT = 10_000;

            refetchHistoryRepeatedInterval[pollingKey] = setInterval(async () => {
                /** Блокируем повторные вызовы, пока не завершился последний (релевантно для медленных соединений) */
                if (pollingRequestsInProgressState[pollingKey]) {
                    return;
                }

                pollingRequestsInProgressState[pollingKey] = true;

                /** Смотрим на статус последней запущенной интеграции */
                const { data } = await fetchHistory(
                    buildIntegrationsTypeStatusSearchParams(syncType, integrationType, { per_page: 1 })
                );
                const isLastRequestEnded = data?.[0]?.status !== 'in_progress' && data?.[0]?.status !== 'not_running';

                /**
                 * Если последняя запущенная интеграция к моменту проверки все еще в процессе:
                 * - ничего не делаем, ждем следующей итерации поллинга
                 */
                if (isLastRequestEnded) {
                    /**
                     * Если последняя запущенная интеграция к моменту проверки успела завершиться:
                     * - завершаем интеграцию
                     */
                    await finishIntegration(fetchHistory, data?.[0]);
                } else {
                    if (syncType !== 'dry_mapping') {
                        /**
                         * Обновляем историю, чтобы пересчитать счетчики успешных и ошибочных результатов
                         */
                        await fetchHistory(integrationStatusSearchParams);
                    }

                    /** Сбрасываем состояние запущенного запроса, чтобы запустился следующий */
                    pollingRequestsInProgressState[pollingKey] = false;
                    onPollingCallback?.(syncType, data?.[0]);
                }
            }, REFETCH_IN_PROGRESS_TIMEOUT);
        } catch (_) {
            showError({ label: 'Произошла ошибка при обновлении статуса интеграции.' });
            toggleProcessing(false);
        } finally {
            pollingRequestsInProgressState[pollingKey] = false;
        }
    }, [
        finishIntegration,
        syncType,
        integrationType,
        integrationStatusSearchParams,
        fetchHistory,
        onPollingCallback,
        pollingKey,
    ]);

    /** Запуск новой интеграции */
    const handleIntegrationStart = useCallback(
        async ({ onError, onSuccess }: { onError?: (error: unknown) => void; onSuccess?: VoidFunction }) => {
            try {
                /** Выставляем флаг запустившейся интеграции, по которому рисуется лоадер в кнопке */
                toggleProcessing(true);

                /**
                 * Запускаем непосредственно интеграцию через API.
                 * excel запускается извне, а для Merchant Center отдельная ручка.
                 */
                const isStartedFromHere = await startIntegrationBySyncType(syncType, integrationType, mapping);

                if (isStartedFromHere) {
                    onSuccess?.();
                }

                /**
                 * Перезапрашиваем данные с историей интеграций (чтобы отобразилась запущенная новая интеграция)
                 * после успешного запуска через некоторое время,
                 * т.к. процесс запускается не сразу, а лишь ставится в очередь temporal
                 * (~200 мс на момент написания комментария, берем с запасом)
                 */
                const TEMPORAL_REFETCH_TIMEOUT = 1_000;

                refetchHistoryTimeout[pollingKey] = setTimeout(
                    fetchHistoryAndPollingOrFinishIntegration,
                    TEMPORAL_REFETCH_TIMEOUT
                );
            } catch (error) {
                isFunction(onError) && error
                    ? onError(error)
                    : showError({ label: 'Произошла ошибка при запуске интеграции.' }, error);

                /** Процесс запуска завершен с ошибкой, можно запускать следующую интеграцию */
                toggleProcessing(false);
            }
        },
        [syncType, fetchHistoryAndPollingOrFinishIntegration, integrationType, mapping, pollingKey]
    );

    return {
        handleIntegrationStart,
        handlePolling,
        handleClean,
        isProcessing,
        canPolling,
    };
};
